<script setup lang="ts">
import { ref, watch } from "vue";
import { gql } from "@apollo/client/core";
import FormGroup from "@/components/form/FormGroup.vue";
import { useMutation, useQuery, useResult } from "@vue/apollo-composable";
import { ClientError, getMessageFromError } from "@/libs/errors";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";

const authStore = useAuthStore();
const errorMessage = ref("");
const loading = ref(false);
const router = useRouter();
const name = ref("");
const phone = ref("");
const location = ref<string | null>(null);

const {
  loading: statesLoading,
  error: statesError,
  result: statesResult,
} = useQuery(gql`
  query States {
    states {
      name
      abbr
    }
  }
`);
const states = useResult(statesResult, null, (data) => data.states);

const { mutate: createProfile } = useMutation(
  gql`
    mutation Mutation($input: UpdateUserInput!) {
      updateMyUser(input: $input) {
        id
      }
    }
  `,
  {
    fetchPolicy: "no-cache",
  }
);

const updateUser = async () => {
  if (loading.value) {
    return;
  }
  loading.value = true;
  errorMessage.value = "";
  try {
    if (!name.value || !location.value) {
      throw new ClientError("Os campos nome e UF são obrigatórios");
    }
    await createProfile({
      input: {
        name: name.value,
        phone: phone.value,
        state: location.value,
      },
    });
    authStore.setState(location.value);
    authStore.setName(name.value);
    router.push("/auth/mobile-register/success");
  } catch (err) {
    errorMessage.value = getMessageFromError(err);
  }
  loading.value = false;
};
</script>

<template>
  <div class="login">
    <div class="container">
      <div class="content-center">
        <div class="row">
          <div class="col col-12 d-block">
            <h2 class="mb-4">
              <a href="https://www.quantofaz.com.br">
                <img src="@/assets/images/logo.svg" alt="logo" width="300" />
              </a>
            </h2>
          </div>
          <div class="col col-12">
            <div class="card-plain">
              <header>
                <div class="progress mb-4">
                  <div
                    class="progress-bar bg-pattern"
                    role="progressbar"
                    style="width: 66.669%"
                    aria-valuenow="25"
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
                <h1>Crie seu perfil</h1>
              </header>
              <form @submit.prevent="updateUser">
                <div v-if="errorMessage" class="alert alert-danger">
                  {{ errorMessage }}
                </div>
                <FormGroup label="Nome" labelFor="name" class="mb-3">
                  <input
                    id="text"
                    type="text"
                    class="form-control"
                    placeholder="Digite seu nome completo"
                    v-model="name"
                    :required="true"
                  />
                </FormGroup>
                <FormGroup
                  label="Telefone (opcional)"
                  labelFor="phone"
                  class="mb-3"
                >
                  <input
                    v-maska="['(##) ####-####', '(##) #####-####']"
                    id="text"
                    type="text"
                    class="form-control"
                    placeholder="(XX) XXXXX-XXXX"
                    v-model="phone"
                  />
                </FormGroup>
                <FormGroup label="UF" labelFor="location" class="mb-3">
                  <select
                    id="location"
                    class="form-select"
                    v-model="location"
                    :required="true"
                  >
                    <option :value="null">Selecione seu estado</option>
                    <option
                      v-for="state of states"
                      :key="`state-${state.abbr}`"
                      :value="state.abbr"
                    >
                      {{ state.name }}
                    </option>
                  </select>
                </FormGroup>
                <div class="buttons d-grid gap-2">
                  <button
                    v-if="loading"
                    type="submit"
                    class="btn btn-primary btn-block"
                    disabled
                  >
                    CONCLUINDO...
                  </button>
                  <button
                    v-else
                    type="submit"
                    class="btn btn-primary btn-block"
                  >
                    FINALIZAR
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.login {
  height: 100vh;
  max-height: 1050px;
  padding: 0;
  position: relative;
  background-color: var(--black-eigengrau);
  /* background-color: #61d; */
}

.login:before {
  /* background-color: #a5c70f; */
  content: "";
  position: absolute;
  z-index: 1;
  width: 100%;
  height: 184px;
  left: 0;
}

.content-center {
  position: relative;
  margin: 0 auto;
  z-index: 99;
  padding: 56px 0 0;
}

h2 {
  font-size: 1.5625rem;
  margin: 0;
  padding: 1.5625rem 0 0.55rem;
  text-align: center;
}

h2 a {
  color: #030303;
  text-decoration: none;
}

.card-plain {
  margin: 0 auto 50px;
  border-radius: 0.5rem;
  max-width: 330px;
  padding: 20px;
  /* background: #daddca; */
  /* -webkit-box-shadow: 0 1px 2px 0 rgb(0 0 0 / 10%); */
  /* box-shadow: 0 1px 2px 0 rgb(0 0 0 / 10%); */
  color: var(--white-ice);
}

.card-plain h1 {
  font-size: 1.5rem;
  line-height: 1.4em;
  margin-bottom: 20px;
  text-align: center;
  color: var(--primary-color);
}

.card-plain .form-control {
  background: var(--black-eigengrau);
  border-color: var(--primary-color);
  color: var(--white-ice);
}

.card-plain .form-control:focus {
  box-shadow: rgba(82, 95, 26, 0.2) 0px 0px 0px 0.25rem;
  color: var(--primary-color);
  font-weight: 500;
}

.card-plain .form-select {
  background-color: var(--black-eigengrau);
  border-color: var(--primary-color);
  color: var(--white-ice);
}

.card-plain .form-select:focus {
  box-shadow: rgba(82, 95, 26, 0.2) 0px 0px 0px 0.25rem;
  color: var(--primary-color);
  font-weight: 500;
}

.btn-check:focus + .btn,
.btn:focus {
  box-shadow: rgba(82, 95, 26, 0.2) 0px 0px 0px 0.25rem;
}

/* .btn-check:focus+.btn-primary, .btn-primary:focus {
    border-color: var(--white-ice);
} */

.input-group-text {
  background: var(--black-eigengrau);
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
}

.input-group-text.active {
  background: var(--primary-color);
  color: var(--black-eigengrau);
}

.card-plain .buttons {
  margin-top: 2rem;
}

.btn-primary {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  color: var(--black-eigengrau);
}

.btn-primary:hover {
  box-shadow: rgba(82, 95, 26, 0.2) 0px 0px 0px 0.25rem;
}

.btn-primary:focus {
  box-shadow: rgba(82, 95, 26, 0.2) 0px 0px 0px 0.25rem;
}
</style>
